// ============================================
// 绑定引擎
// 评估数据绑定表达式并应用到节点属性
// ============================================

import type {
  DataBinding,
  SceneDocument,
  SceneNode,
} from '../types/designer';
import type {
  BindingExpression,
  BindingEvaluationResult,
  ExpressionType,
} from '../types/databinding';
import { DATA_TRANSFORMS } from '../types/databinding';
import { EventEmitter } from './EventEmitter';

export class BindingEngine extends EventEmitter {
  private document: SceneDocument | null = null;
  private bindingsByNode: Map<string, DataBinding[]> = new Map();
  private bindingsByDatasource: Map<string, Array<{ nodeId: string; binding: DataBinding }>> = new Map();
  private lastValues: Map<string, any> = new Map();
  private evaluateCount = 0;
  private errorCount = 0;

  /**
   * 加载场景文档并解析绑定关系
   */
  loadDocument(doc: SceneDocument): void {
    this.document = doc;
    this.bindingsByNode.clear();
    this.bindingsByDatasource.clear();
    this.lastValues.clear();

    for (const node of doc.nodes) {
      if (node.bindings && node.bindings.length > 0) {
        this.bindingsByNode.set(node.id, node.bindings);

        for (const binding of node.bindings) {
          if (!this.bindingsByDatasource.has(binding.datasource)) {
            this.bindingsByDatasource.set(binding.datasource, []);
          }
          this.bindingsByDatasource.get(binding.datasource)!.push({ nodeId: node.id, binding });
        }
      }
    }

    this.emit('document:loaded', { nodeCount: doc.nodes.length });
  }

  /**
   * 处理数据更新 — 评估所有绑定到该数据源的绑定
   */
  processDataUpdate(datasourceId: string, data: any): BindingEvaluationResult[] {
    const entries = this.bindingsByDatasource.get(datasourceId) || [];
    const results: BindingEvaluationResult[] = [];

    for (const { nodeId, binding } of entries) {
      const result = this.evaluateBinding(binding, data);
      result.nodeId = nodeId;
      results.push(result);
    }

    this.emit('data:processed', { datasourceId, results });
    return results;
  }

  /**
   * 评估单个绑定
   */
  evaluateBinding(binding: DataBinding, data: any): BindingEvaluationResult {
    const startTime = Date.now();
    this.evaluateCount++;

    try {
      let value: any = data;

      // 应用表达式
      if (binding.expression) {
        const expr = binding.expression.trim();

        // 模板表达式 {{...}}
        const templateMatch = expr.match(/^\{\{(.+?)\}\}$/);
        if (templateMatch) {
          try {
            const fn = new Function('data', `return ${templateMatch[1]}`);
            value = fn(data);
          } catch { value = data; }
        }
        // data.xxx 路径引用
        else if (expr.startsWith('data.')) {
          const path = expr.slice(5);
          value = this.getNestedValue(data, path);
        }
        // 其他表达式
        else {
          value = this.evaluateExpression(expr, data);
        }
      }

      // 应用数据转换
      if (binding.transform) {
        value = this.applyTransform(binding.transform, value, data);
      }

      // 缓存最后的值
      this.lastValues.set(binding.id, value);

      return {
        bindingId: binding.id,
        nodeId: '', // 由调用方填充
        property: binding.property,
        value,
        success: true,
        timestamp: startTime,
      };
    } catch (err: any) {
      this.errorCount++;
      return {
        bindingId: binding.id,
        nodeId: '',
        property: binding.property,
        value: null,
        success: false,
        error: err.message,
        timestamp: startTime,
      };
    }
  }

  /**
   * 评估表达式
   *
   * 支持的格式:
   *   - 直接引用: "data.temperature"
   *   - 模板表达式: "{{data.temperature * 2}}"
   *   - 条件表达式: "{{data.value > 100 ? 'high' : 'low'}}"
   */
  evaluateExpression(expression: string, data: any): any {
    if (!expression) return data;

    const templateMatch = expression.match(/^\{\{(.+?)\}\}$/);
    if (templateMatch) {
      try {
        const fn = new Function('data', `return ${templateMatch[1]}`);
        return fn(data);
      } catch { return undefined; }
    }

    if (expression.startsWith('data.')) {
      const path = expression.slice(5);
      return this.getNestedValue(data, path);
    }

    if (expression === 'true') return true;
    if (expression === 'false') return false;
    if (!isNaN(Number(expression))) return Number(expression);

    return expression;
  }

  /**
   * 应用数据转换
   */
  applyTransform(transformName: string, value: any, data?: any): any {
    const transformFn = DATA_TRANSFORMS[transformName];
    if (!transformFn) {
      console.warn(`Unknown transform: ${transformName}`);
      return value;
    }
    return transformFn(value, data);
  }

  /**
   * 获取绑定的最后值
   */
  getLastValue(bindingId: string): any {
    return this.lastValues.get(bindingId);
  }

  /**
   * 获取节点的所有绑定
   */
  getNodeBindings(nodeId: string): DataBinding[] {
    return this.bindingsByNode.get(nodeId) || [];
  }

  /**
   * 获取数据源的所有绑定
   */
  getDatasourceBindings(datasourceId: string): DataBinding[] {
    return (this.bindingsByDatasource.get(datasourceId) || []).map(e => e.binding);
  }

  /**
   * 注册单个绑定（用于 UI 动态添加的绑定）
   */
  registerBinding(nodeId: string, binding: DataBinding): void {
    // 注册到 bindingsByNode
    const existing = this.bindingsByNode.get(nodeId) || [];
    this.bindingsByNode.set(nodeId, [...existing, binding]);

    // 注册到 bindingsByDatasource
    if (!this.bindingsByDatasource.has(binding.datasource)) {
      this.bindingsByDatasource.set(binding.datasource, []);
    }
    this.bindingsByDatasource.get(binding.datasource)!.push({ nodeId, binding });
  }

  /**
   * 注销单个绑定
   */
  unregisterBinding(bindingId: string): void {
    // 从 bindingsByNode 中移除
    for (const [nodeId, bindings] of this.bindingsByNode) {
      const filtered = bindings.filter(b => b.id !== bindingId);
      if (filtered.length === 0) {
        this.bindingsByNode.delete(nodeId);
      } else {
        this.bindingsByNode.set(nodeId, filtered);
      }
    }

    // 从 bindingsByDatasource 中移除
    for (const [dsId, entries] of this.bindingsByDatasource) {
      const filtered = entries.filter(e => e.binding.id !== bindingId);
      if (filtered.length === 0) {
        this.bindingsByDatasource.delete(dsId);
      } else {
        this.bindingsByDatasource.set(dsId, filtered);
      }
    }
  }

  /**
   * 获取所有绑定（调试用）
   */
  getAllBindings(): Array<{ nodeId: string; binding: DataBinding }> {
    const result: Array<{ nodeId: string; binding: DataBinding }> = [];
    for (const [nodeId, bindings] of this.bindingsByNode) {
      for (const binding of bindings) {
        result.push({ nodeId, binding });
      }
    }
    return result;
  }

  /**
   * 获取统计信息
   */
  getStats() {
    return {
      evaluateCount: this.evaluateCount,
      errorCount: this.errorCount,
      bindingCount: Array.from(this.bindingsByNode.values()).reduce(
        (sum, bindings) => sum + bindings.length,
        0
      ),
      nodeCount: this.bindingsByNode.size,
      datasourceCount: this.bindingsByDatasource.size,
    };
  }

  /**
   * 重置统计
   */
  resetStats(): void {
    this.evaluateCount = 0;
    this.errorCount = 0;
  }

  /**
   * 工具方法: 获取嵌套对象属性值
   */
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return obj;
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      current = current[part];
    }
    return current;
  }

  /**
   * 销毁引擎
   */
  destroy(): void {
    this.document = null;
    this.bindingsByNode.clear();
    this.bindingsByDatasource.clear();
    this.lastValues.clear();
    this.removeAllListeners();
  }
}
